import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ThunkConfig, ErrorField } from "store";
import {
  IOSLogInRequest,
  IOSLogInResponse,
  resetPasswordRequest,
  confirmEmailRequest,
  confirmEmailResponse,
} from "models/signIn";
import { signInService } from "services/SignInService";

interface ExtraLogInData {
  isRememberMe?: boolean;
  request: IOSLogInRequest;
}

export const logIn = createAsyncThunk<
  IOSLogInResponse,
  ExtraLogInData,
  ThunkConfig<ErrorField[]>
>("signIn/logIn", async (data, { rejectWithValue }) => {
  try {
    const { email, password } = data.request;
    const isRememberMe = data.isRememberMe;
    const response = await signInService.logIn({ email, password });
    if (isRememberMe) {
      localStorage.setItem("Authorization", response.accessToken);
      localStorage.setItem("clientId", response.clientId);
      localStorage.setItem("email", response.email);
      localStorage.setItem("type", response.type);
    } else if (!isRememberMe) {
      sessionStorage.setItem("Authorization", response.accessToken);
      sessionStorage.setItem("clientId", response.clientId);
      sessionStorage.setItem("email", response.email);
      sessionStorage.setItem("type", response.type);
    }

    return response;
  } catch (e: any) {
    return rejectWithValue(e.response.data.errors);
  }
});

export const resetPassword = createAsyncThunk<
  { isReset: boolean },
  resetPasswordRequest,
  ThunkConfig<ErrorField[]>
>("signIn/resetPassword", async (data, { rejectWithValue }) => {
  try {
    const response = await signInService.resetPassword(data);
    return response;
  } catch (e: any) {
    return rejectWithValue(e.response.data.errors);
  }
});

export const confirmEmail = createAsyncThunk<
  confirmEmailResponse,
  confirmEmailRequest,
  ThunkConfig<ErrorField[]>
>("signIn/confirmEmail", async (data, { rejectWithValue }) => {
  try {
    const response = await signInService.confirmEmail(data);
    return response;
  } catch (e: any) {
    return rejectWithValue(e.response.data.errors);
  }
});

export interface SignInSchema {
  email: string;
  password: string;
  isOpenPopup: boolean;
  isOpenServerErrorPopupSignIn?: boolean;
  errorHeaderSignIn?: string;
  errorTextSignIn?: string;
  isResetPasswordSuccess?: boolean;
  isOpenConfirmPopUp?: boolean;
}
const initialState: SignInSchema = {
  email: "",
  password: "",
  isOpenPopup: false,
};
export const signInSlice = createSlice({
  name: "signIn",
  initialState,
  reducers: {
    setIsOpenPopup: (state, action) => {
      return {
        ...state,
        isOpenPopup: action.payload,
      };
    },
    setIsOpenServerErrorPopupSignIn: (state, action) => {
      return {
        ...state,
        isOpenServerErrorPopupSignIn: action.payload,
      };
    },
    setIsOpenConfirmPopUp: (state, action) => {
      return {
        ...state,
        isOpenConfirmPopUp: action.payload,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(logIn.fulfilled, (state, action) => {
      window.location.href = "/cabinet";
    });
    builder.addCase(logIn.rejected, (state, action) => {
      if (
        action.payload?.find(
          (error: any) => error.message === "EMAIL_NOT_CONFIRMED_ERROR"
        )
      ) {
        return {
          ...state,
          isOpenServerErrorPopupSignIn: true,
          errorHeaderSignIn: "anErrorOccurred",
          errorTextSignIn: "confirmedEmailError",
        };
      } else if (
        action.payload?.find(
          (error: any) => error.message === "INVALID_USERNAME_OR_PASSWORD_ERROR"
        )
      ) {
        return {
          ...state,
          isOpenServerErrorPopupSignIn: true,
          errorHeaderSignIn: "anErrorOccurred",
          errorTextSignIn: "invalidEmailOrPasswordError",
        };
      } else if (
        action.payload?.find(
          (error: any) =>
            error.message === "STRING_LENGTH_IS_TOO_SHORT" &&
            error.field === "password"
        )
      ) {
        return {
          ...state,
          isOpenServerErrorPopupSignIn: true,
          errorHeaderSignIn: "anErrorOccurred",
          errorTextSignIn: "invalidPasswordError",
        };
      } else if (
        action.payload?.find(
          (error: any) =>
            error.message === "CLIENT_NOT_FOUND_ERROR" 
        )
      ) {
        return {
          ...state,
          isOpenServerErrorPopupSignIn: true,
          errorHeaderSignIn: "anErrorOccurred",
          errorTextSignIn: "userNotFoundError",
        };
      } else if (
        action.payload?.find(
          (error: any) => error.message === "CLIENT_NOT_REGISTERED_ERROR"
        )
      ) {
        return {
          ...state,
          isOpenServerErrorPopupSignIn: true,
          errorHeaderSignIn: "anErrorOccurred",
          errorTextSignIn: "userNotFoundError",
        };
      } else {
        return {
          ...state,
          isOpenServerErrorPopupSignIn: true,
          errorHeaderSignIn: "anErrorOccurred",
          errorTextSignIn: "serverError",
        };
      }
    });
    builder.addCase(resetPassword.fulfilled, (state, action) => {
      return {
        ...state,
        isOpenPopup: true,
        isResetPasswordSuccess: action.payload.isReset,
      };
    });
    builder.addCase(resetPassword.rejected, (state, action) => {
      if (
        action.payload?.find(
          (error: any) => error.message === "CLIENT_NOT_FOUND_ERROR"
        )
      ) {
        return {
          ...state,
          isOpenServerErrorPopupSignIn: true,
          errorHeaderSignIn: "anErrorOccurred",
          errorTextSignIn: "userNotFoundError",
        };
      } else
        return {
          ...state,
          isOpenServerErrorPopupSignIn: true,
          errorHeaderSignIn: "anErrorOccurred",
          errorTextSignIn: "serverError",
        };
    });
    builder.addCase(confirmEmail.fulfilled, (state, action) => {
      const baseUrl = window.location.origin;
      const currentParams = new URLSearchParams();
      currentParams.set("form", "sign");
      const newUrl = `${baseUrl}?${currentParams.toString()}`;
      window.location.href = newUrl;
      return {
        ...state,
        isOpenConfirmPopUp: true,
      };
    });
    builder.addCase(confirmEmail.rejected, (state, action) => {
      const newUrl = window.location.origin + window.location.pathname;
      window.history.replaceState(null, "", newUrl);
      if (
        action.payload?.find(
          (error: any) => error.message === "CLIENT_NOT_FOUND_ERROR"
        )
      ) {
        return {
          ...state,
          isOpenServerErrorPopupSignIn: true,
          errorHeaderSignIn: "anErrorOccurred",
          errorTextSignIn: "userNotFoundError",
        };
      } else if (
        action.payload?.find(
          (error: any) => error.message === "EMAIL_ALREADY_CONFIRMED_ERROR"
        )
      ) {
        return {
          ...state,
          isOpenServerErrorPopupSignIn: true,
          errorHeaderSignIn: "anErrorOccurred",
          errorTextSignIn: "emailAlreadyExistsError",
        };
      } else {
        return {
          ...state,
          isOpenServerErrorPopupSignIn: true,
          errorHeaderSignIn: "anErrorOccurred",
          errorTextSignIn: "serverError",
        };
      }
    });
  },
});

export const { actions: signInActions } = signInSlice;
export const { reducer: signInReducer } = signInSlice;
